/**
 * Claim a real username for a social user created with a placeholder handle.
 *
 * POSTs to the auth service, which sets `username` + `displayUsername` and
 * clears the `username_pending` claim on success. Returns a discriminated
 * result so the caller can render inline messages instead of catching throws.
 *
 * Goes through `authFetch` so the session travels on native too, where there
 * is no cookie jar for `credentials: 'include'` to draw on.
 */

import { authFetch, safeJson } from './auth-fetch';

const CLAIM_PATH = '/api/auth/username/claim';

export type ClaimUsernameFailureReason =
  | 'taken'
  | 'invalid'
  | 'unauthenticated'
  | 'unknown';

export type ClaimUsernameResult =
  | { ok: true; username: string }
  | { ok: false; reason: ClaimUsernameFailureReason; message: string };

const FAILURE_MESSAGES: Record<ClaimUsernameFailureReason, string> = {
  taken: 'That username is already taken.',
  invalid: 'That username is not valid. Use 3-30 letters and numbers.',
  unauthenticated: 'Your session expired. Please sign in again.',
  unknown: 'Could not save your username. Please try again.',
};

const failure = (reason: ClaimUsernameFailureReason): ClaimUsernameResult => ({
  ok: false,
  reason,
  message: FAILURE_MESSAGES[reason],
});

export async function claimUsername(username: string): Promise<ClaimUsernameResult> {
  let response: Response;
  try {
    response = await authFetch(CLAIM_PATH, {
      method: 'POST',
      body: { username },
    });
  } catch {
    return failure('unknown');
  }

  if (response.status === 200) {
    const data = await safeJson(response);
    const claimed = typeof data.username === 'string' ? data.username : username;
    return { ok: true, username: claimed };
  }
  if (response.status === 409) return failure('taken');
  if (response.status === 400) return failure('invalid');
  if (response.status === 401) return failure('unauthenticated');
  return failure('unknown');
}
